import type { IPublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { tokenIsExpired, useAuthentication } from '@bestseller-bit/frontend-community.utilities.authentication';
import { useCallback } from 'react';
import { setToken } from 'src/utils/token/tokenUtils';

const pendingTokens = new WeakMap<IPublicClientApplication, Promise<string | undefined>>();
const pendingRefreshes = new WeakMap<IPublicClientApplication, Promise<string | undefined>>();

export const useAcquireIdToken = () => {
    const { acquireIdToken, account, interactionType } = useAuthentication();
    const { instance, inProgress } = useMsal();

    return useCallback(
        function acquireToken(forceRefresh = false): Promise<string | undefined> {
            const pendingRequests = forceRefresh ? pendingRefreshes : pendingTokens;
            const pendingToken = pendingRefreshes.get(instance) ?? pendingRequests.get(instance);
            if (pendingToken !== undefined) return pendingToken;

            const acquire = async (): Promise<string | undefined> => {
                if (inProgress !== InteractionStatus.None) return undefined;

                if (!forceRefresh) {
                    const token = await acquireIdToken();
                    if (!token || !tokenIsExpired(token)) return token;
                    return acquireToken(true);
                }

                if (!account) return acquireIdToken();

                const request = {
                    account,
                    scopes: ['User.Read', 'profile', 'openid', 'offline_access', 'email'],
                    forceRefresh: true,
                };

                let response;
                try {
                    response = await instance.acquireTokenSilent(request);
                } catch (error) {
                    if (!(error instanceof InteractionRequiredAuthError)) throw error;

                    if (interactionType !== InteractionType.Popup) {
                        await instance.acquireTokenRedirect(request);
                        return undefined;
                    }
                    response = await instance.acquireTokenPopup(request);
                }

                if (response.account) instance.setActiveAccount(response.account);
                setToken(response.idToken);
                return response.idToken;
            };

            const promise = acquire().finally(() => pendingRequests.delete(instance));
            pendingRequests.set(instance, promise);
            return promise;
        },
        [account, acquireIdToken, inProgress, instance, interactionType]
    );
};
